import React from 'react';
import { Phone, CheckCircle, Star } from 'lucide-react';
import { Button } from './ui/button';
import { businessInfo } from '../mockData';

const Hero = () => {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <section id="home" className="relative min-h-[90vh] flex items-center overflow-hidden bg-gradient-to-br from-slate-50 via-orange-50 to-slate-50">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, rgb(234, 88, 12) 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }}></div>
      </div>

      <div className="container mx-auto px-4 py-20 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            {/* Trust Badges */}
            <div className="flex flex-wrap gap-3">
              <div className="inline-flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm border border-orange-200">
                <Star className="text-orange-500 fill-orange-500" size={18} />
                <span className="text-sm font-semibold text-gray-700">DOT# 3310412 Licensed & Insured</span>
              </div>
              <div className="inline-flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm border border-orange-200">
                <CheckCircle className="text-orange-500" size={18} />
                <span className="text-sm font-semibold text-gray-700">5x Best of Lakeshore</span>
              </div>
            </div>

            {/* Main Headline */}
            <div className="space-y-4">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                Professional Moving & Junk Removal in{' '}
                <span className="text-orange-600">Manitowoc</span>
              </h1>
              <p className="text-lg md:text-xl text-gray-600 leading-relaxed">
                Accurate, affordable, and trustworthy services. We handle your moving, junk removal, and small demolition needs with care and professionalism.
              </p>
            </div>

            {/* Features List */}
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                'Licensed & Insured',
                'Experienced Team',
                'Affordable Pricing',
                'Local Experts'
              ].map((feature, index) => (
                <div key={index} className="flex items-center gap-2">
                  <CheckCircle className="text-orange-600 flex-shrink-0" size={20} />
                  <span className="text-gray-700 font-medium">{feature}</span>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Button 
                onClick={() => scrollToSection('contact')}
                className="bg-orange-600 hover:bg-orange-700 text-white px-8 py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
              >
                Get Free Quote
              </Button>
              <Button 
                variant="outline"
                onClick={() => window.location.href = `tel:${businessInfo.phone}`}
                className="border-2 border-orange-600 text-orange-600 hover:bg-orange-50 px-8 py-6 text-lg font-semibold transition-all duration-300"
              >
                <Phone className="mr-2" size={20} />
                Call Now
              </Button>
            </div>

            {/* Phone Number Display */}
            <div className="pt-4 border-t border-gray-200">
              <p className="text-sm text-gray-600 mb-1">Call us today for immediate assistance:</p>
              <a href={`tel:${businessInfo.phone}`} className="text-2xl md:text-3xl font-bold text-orange-600 hover:text-orange-700 transition-colors">
                {businessInfo.phone}
              </a>
            </div>
          </div>

          {/* Right Image */}
          <div className="relative lg:block">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-white p-8">
              <img 
                src="https://customer-assets.emergentagent.com/job_moving-depot/artifacts/wwg7jput_all_out_nosh.png"
                alt="All Out Moving - Going Big, Going Strong"
                className="w-full h-[500px] object-contain"
              />
              {/* Overlay Card */}
              <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-sm rounded-xl p-6 shadow-xl">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Trusted by 500+ customers</p>
                    <div className="flex items-center gap-1">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="text-orange-500 fill-orange-500" size={18} />
                      ))}
                      <span className="ml-2 text-gray-900 font-bold">5.0 Rating</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Floating Badge */}
            <div className="absolute -top-4 -right-4 bg-orange-600 text-white rounded-full w-32 h-32 flex flex-col items-center justify-center shadow-xl">
              <span className="text-3xl font-bold">24/7</span>
              <span className="text-sm">Available</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
